import React from 'react';
import '@mui/material';
import Item from '@mui/material/Grid';
import './../Dashboard.css';
import Api from '../../../utils/Api';
import UserGrid from '../../../composant/Dashboard/UserGrid';
import Loading from '../../../composant/Loading/LoadingComponent';
import {
  Tabs,
  Tab,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import InformationPopup from '../../../composant/Dashboard/Popups/InformationPopup';
import { RoleIds } from '../../../policies/Role';

// Page modifier par Frédérick BETHERMAT - 24-03-2026

/**
 * Onglet de gestion des utilisateurs du tableau de bord.
 *
 * Affiche la liste des utilisateurs actifs et désactivés dans deux onglets distincts.
 * Selon le rôle de l'utilisateur connecté :
 *  - Un administrateur (Admin / AdminContact) voit les deux onglets et peut supprimer
 *    tous les liens badges-étudiants.
 *  - Un enseignant (Teacher) ne voit que les utilisateurs actifs, sans les onglets.
 */
class UsersTab extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeUsers: [], // Liste des utilisateurs actifs récupérés depuis l'API
      inactiveUsers: [], // Liste des utilisateurs désactivés récupérés depuis l'API
      charge: false, // Indique si le chargement initial des données est terminé
      tabIndex: 0, // Index de l'onglet actuellement sélectionné (0 = actifs, 1 = désactivés)
      confirmDelete: false, // Contrôle l'ouverture du dialogue de confirmation de suppression
      openSuccessPopup: false, // Contrôle l'affichage de la popup de succès
      openErrorPopup: false, // Contrôle l'affichage de la popup d'erreur
      role: 0, // Identifiant du rôle de l'utilisateur connecté
    };
  }

  /**
   * Au montage du composant, on récupère simultanément :
   * - Le rôle de l'utilisateur connecté
   * - La liste des utilisateurs actifs
   * - La liste des utilisateurs désactivés
   */
  componentDidMount() {
    this.fetchUser();
    this.fetchActiveUsers();
    this.fetchInactiveUsers();
  }

  /**
   * Récupère les informations de l'utilisateur connecté,
   * notamment son rôle, afin d'adapter l'affichage en conséquence.
   */
  fetchUser = () => {
    Api.get('/auth/current_user')
      .then((response) => {
        this.setState({ role: response.data.role_id });
      })
      .catch((error) => {
        console.error(error);
      });
  };

  /**
   * Récupère la liste des utilisateurs actifs (active = 1) depuis l'API
   * et met à jour le state.
   */
  fetchActiveUsers = () => {
    Api.get('/user/active/1').then((res) => {
      this.setState({ activeUsers: res.data.users });
    });
  };

  /**
   * Récupère la liste des utilisateurs désactivés (active = 0) depuis l'API,
   * met à jour le state et marque le chargement initial comme terminé.
   */
  fetchInactiveUsers = () => {
    Api.get('/user/active/0').then((res) => {
      this.setState({ inactiveUsers: res.data.users, charge: true });
    });
  };

  /**
   * Gère le changement d'onglet entre utilisateurs actifs et désactivés.
   * @param {React.SyntheticEvent} event - L'événement déclencheur
   * @param {number} newValue - L'index du nouvel onglet sélectionné
   */
  handleTabChange = (event, newValue) => {
    this.setState({ tabIndex: newValue });
  };

  /**
   * Rafraîchit simultanément les listes d'utilisateurs actifs et désactivés.
   * Utilisé après une modification affectant potentiellement les deux listes
   * (ex : activation / désactivation d'un compte).
   */
  refreshAllUsers = () => {
    this.fetchActiveUsers();
    this.fetchInactiveUsers();
  };

  /**
   * Envoie une requête DELETE pour supprimer tous les liens entre badges et utilisateurs.
   * Affiche une popup de succès ou d'erreur selon le résultat de l'appel.
   */
  deleteLink = () => {
    Api.delete(`/user/delete-all-links`)
      .then(() => {
        this.setState({ confirmDelete: false, openSuccessPopup: true });
      })
      .catch(() => {
        this.setState({ confirmDelete: false, openErrorPopup: true });
      });
  };

  /**
   * Ferme les popups d'information (succès et erreur) en réinitialisant leurs états.
   */
  onClosePopupInformation = () => {
    this.setState({ openSuccessPopup: false, openErrorPopup: false });
  };

  /**
   * Vérifie si l'utilisateur connecté possède un rôle administrateur.
   * @returns {boolean} True si le rôle est Admin ou AdminContact
   */
  isAdmin = () => {
    const { role } = this.state;
    return role === RoleIds.Admin || role === RoleIds.AdminContact;
  };

  /**
   * Vérifie si l'utilisateur connecté est un enseignant.
   * @returns {boolean} True si le rôle est Teacher
   */
  isTeacher = () => {
    return this.state.role === RoleIds.Teacher;
  };

  /**
   * Génère le composant UserGrid avec les propriétés communes.
   * @param {Array} rows - La liste des utilisateurs à afficher dans la grille
   * @param {Function} refreshUsers - Fonction de rafraîchissement propre à la liste courante
   * @returns {JSX.Element} Le composant UserGrid configuré
   */
  renderUserGrid = (rows, refreshUsers) => {
    return (
      <UserGrid
        rows={rows}
        refreshUsers={refreshUsers}
        refreshAllUsers={this.refreshAllUsers}
        role={this.state.role}
      />
    );
  };

  render() {
    const { tabIndex, activeUsers, inactiveUsers, charge } = this.state;

    return (
      <Item className="bordered">
        <h4>Liste des utilisateurs</h4>

        {/*
          Affichage conditionnel selon le rôle :
          - Enseignant : grille simple des utilisateurs actifs uniquement
          - Autres rôles : deux onglets (actifs / désactivés)
        */}
        {this.isTeacher() ? (
          <Box sx={{ mt: 2 }}>
            {this.renderUserGrid(activeUsers, this.fetchActiveUsers)}
          </Box>
        ) : (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabIndex} onChange={this.handleTabChange}>
                <Tab label="Utilisateurs actifs" />
                <Tab label="Utilisateurs désactivés" />
              </Tabs>
            </Box>

            {/* Contenu de l'onglet sélectionné */}
            <Box sx={{ mt: 2 }}>
              {tabIndex === 0 &&
                this.renderUserGrid(activeUsers, this.fetchActiveUsers)}
              {tabIndex === 1 &&
                this.renderUserGrid(inactiveUsers, this.fetchInactiveUsers)}
            </Box>
          </>
        )}

        {/* Bouton de suppression des liens badges-étudiants, visible uniquement pour les admins */}
        {this.isAdmin() && (
          <Box className="linkDeleteButton">
            <Button
              variant="contained"
              onClick={() => this.setState({ confirmDelete: true })}
            >
              Supprimer les liens badges-étudiants
            </Button>
          </Box>
        )}

        {/* Dialogue de confirmation avant la suppression de tous les liens badges-utilisateurs */}
        <Dialog
          open={this.state.confirmDelete}
          onClose={() => this.setState({ confirmDelete: false })}
        >
          <DialogTitle>Confirmer la suppression</DialogTitle>
          <DialogContent>
            Êtes-vous sûr de vouloir supprimer tous les liens
            badges-utilisateurs ?
          </DialogContent>
          <DialogActions>
            <Button onClick={this.deleteLink} variant="contained" color="error">
              Confirmer
            </Button>
            <Button
              onClick={() => this.setState({ confirmDelete: false })}
              variant="outlined"
            >
              Annuler
            </Button>
          </DialogActions>
        </Dialog>

        {/* Popup affichée après une suppression réussie */}
        <InformationPopup
          onClose={this.onClosePopupInformation}
          isOpen={this.state.openSuccessPopup}
          message="Réussite de la suppression"
          severity="success"
        />

        {/* Popup affichée en cas d'échec de la suppression */}
        <InformationPopup
          onClose={this.onClosePopupInformation}
          isOpen={this.state.openErrorPopup}
          message="Échec de la suppression"
          severity="error"
        />

        {/* Indicateur de chargement affiché tant que les données ne sont pas prêtes */}
        {!charge && <Loading />}
      </Item>
    );
  }
}

export default UsersTab;
